import { isEnglishTestRoute, useLanguage } from '@/lib/i18n'
import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { R5Shell } from '@/components/r5/R5Shell'
import { isR5PublicPath, isR5ShellPath, isR5ToolPath } from '@/components/r5/route-chrome'
import { RequireAuth } from '@/features/auth/RequireAuth'
import { RequireRole } from '@/features/auth/RequireRole'
import { useAuth } from '@/features/auth/auth-context'
import { LanguageToggle } from '@/components/shell/LanguageToggle'
import { ReleaseBanner } from '@/components/shell/ReleaseBanner'
import { ViewAsBanner } from '@/components/shell/ViewAsBanner'
import { StaffTicketBoundary } from '@/features/staff-tickets/StaffTicketBoundary'
import { LibraryTaskBanner } from '@/features/tasks/LibraryTaskBanner'
import { TaskGate } from '@/features/tasks/TaskGate'
import { tasksEnabled } from '@/features/tasks/tasks-flag'
import { RouteLoading } from '@/features/system/RouteLoading'

const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/features/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/features/auth/ResetPasswordPage'))
const ActivateAccountPage = lazy(() => import('@/features/auth/ActivateAccountPage'))
const ActivateStaffPage = lazy(() => import('@/features/auth/ActivateStaffPage'))
const AdminForgotPasswordPage = lazy(() => import('@/features/auth/AdminForgotPasswordPage'))
const AdminResetPasswordPage = lazy(() => import('@/features/auth/AdminResetPasswordPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const ExamsPage = lazy(() => import('@/features/exams/ExamsPage'))
const RunnerPage = lazy(() => import('@/features/runner/RunnerPage'))
const ResultsPage = lazy(() => import('@/features/results/ResultsPage'))
const ResultDetailPage = lazy(() => import('@/features/results/ResultDetailPage'))
const LeaderboardPage = lazy(() => import('@/features/leaderboard/LeaderboardPage'))
const AchievementsPage = lazy(() => import('@/features/achievements/AchievementsPage'))
const ProfilePage = lazy(() => import('@/features/profile/ProfilePage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))
const PlanPage = lazy(() => import('@/features/plan/PlanPage'))
const AdminShell = lazy(() => import('@/features/admin/AdminShell'))
const AdminExamsListPage = lazy(() => import('@/features/admin/AdminExamsListPage'))
const AdminExamsArchivedPage = lazy(() => import('@/features/admin/AdminExamsArchivedPage'))
const AdminQuestionBankPage = lazy(() => import('@/features/admin/AdminQuestionBankPage'))
const AdminExamWizardPage = lazy(() => import('@/features/admin/AdminExamWizardPage'))
const AdminSubmissionsPage = lazy(() => import('@/features/admin/review/AdminSubmissionsPage'))
const NeedsAttentionPage = lazy(() => import('@/features/admin/needs-attention/NeedsAttentionPage'))
const AdminResultReviewPage = lazy(() => import('@/features/admin/review/AdminResultReviewPage'))
const ReviewQueuePage = lazy(() => import('@/features/admin/review-queue/ReviewQueuePage'))
const ReviewItemPage = lazy(() => import('@/features/admin/review-queue/ReviewItemPage'))
const WritingReviewQueuePage = lazy(() => import('@/features/admin/writing-review/WritingReviewQueuePage'))
const WritingReviewPage = lazy(() => import('@/features/admin/writing-review/WritingReviewPage'))
const AdminDashboardPage = lazy(() => import('@/features/admin/overview/AdminDashboardPage'))
const AdminDraftsRedirect = lazy(() => import('@/features/admin/AdminDraftsRedirect'))
const AdminStudentsListPage = lazy(() => import('@/features/admin/people/StudentsListPage'))
const AdminStudentDetailPage = lazy(() => import('@/features/admin/people/StudentDetailPage'))
const AdminAddStudentPage = lazy(() => import('@/features/admin/people/AddStudentPage'))
const AdminsListPage = lazy(() => import('@/features/admin/people/AdminsListPage'))
const AdminAddAdminPage = lazy(() => import('@/features/admin/people/AddAdminPage'))
const AdminEmployeeDetailPage = lazy(() => import('@/features/admin/people/EmployeeDetailPage'))
const AdminBulkUploadRetiredPage = lazy(() => import('@/features/admin/AdminBulkUploadRetiredPage'))
const AdminAnnouncementsPage = lazy(() => import('@/features/admin/AdminAnnouncementsPage'))
const AdminTasksPage = lazy(() => import('@/features/admin/tasks/AdminTasksPage'))
const OnboardingStartPage = lazy(() => import('@/features/tasks/OnboardingStartPage'))
const NotFoundPage = lazy(() => import('@/features/system/NotFoundPage'))
const LandingPage = lazy(() => import('@/features/landing/LandingPage'))
const ScoreCalculatorsPage = lazy(() => import('@/features/calculators/ScoreCalculatorsPage'))
const StudyDestinationsPage = lazy(() => import('@/features/destinations/StudyDestinationsPage'))
const KeepTalkingPage = lazy(() => import('@/features/keep-talking/KeepTalkingPage'))
// The r5 component story page: dev builds and preview deploys only. The ternary is folded away at build time on every other
// build, so the page and its chunk are not in a production bundle.
const r5StoryEnabled = import.meta.env.DEV || import.meta.env.VITE_API_ENVIRONMENT === 'dev'
const R5StoryPage = r5StoryEnabled ? lazy(() => import('@/dev/R5StoryPage')) : null
const R5MotionLab = r5StoryEnabled ? lazy(() => import('@/dev/R5MotionLab')) : null
// The school pages are real in every build: a teacher or an Edvantage staff admin, on `/mocktest/school/*`. The sample school
// (DECISIONS 16) is a second entry that exists only in demo-enabled builds. The ternary is folded at build time, so a
// production bundle has neither the sample nor its chunk (src/deploy/school-demo-gate.test.ts proves it).
const schoolDemoEnabled = import.meta.env.VITE_ENABLE_DEMO_SESSION === 'true'
const SchoolApp = lazy(() => import('@/features/school/SchoolApp'))
const SchoolDemoApp = schoolDemoEnabled ? lazy(() => import('@/features/school/demo/SchoolDemoApp')) : null
const TicketInboxPage = lazy(() => import('@/features/staff-tickets/TicketPages').then((module) => ({ default: module.TicketInboxPage })))
const TicketThreadPage = lazy(() => import('@/features/staff-tickets/TicketPages').then((module) => ({ default: module.TicketThreadPage })))

/**
 * Keep Talking is a first-class student surface when the build-time flag is
 * exactly 'true'. An absent, empty or misspelled value leaves the route
 * unregistered, so `/keep-talking` is an honest 404 rather than a half-wired
 * page. Production ships the flag on; do not flip it off to "be safe".
 */
const keepTalkingEnabled = import.meta.env.VITE_ENABLE_KEEP_TALKING === 'true'

/**
 * `/school/*`: the sample school for a demo session (demo builds only), otherwise the real school, whose own guard sends
 * everyone who is not a teacher or a staff admin where they belong. A demo profile on a build without demo sessions cannot
 * exist; if one ever did, it goes to sign-in rather than around in a circle.
 */
function SchoolEntry() {
  const { profile, loading } = useAuth()
  if (!loading && profile?.user.role === 'school_demo') {
    return SchoolDemoApp ? <SchoolDemoApp /> : <Navigate to="/login" replace />
  }
  return <SchoolApp />
}

function PageFallback() {
  // A page-shaped skeleton on student and public routes; the exam room and the admin pages keep the plain status line.
  return <RouteLoading />
}

function InterfaceControls() {
  const { pathname } = useLocation()
  const exam = isEnglishTestRoute(pathname)
  // r5 surfaces render their own language switch (avatar menu); the strip stays mounted, hidden, because its toggle also
  // keeps <html lang dir> in step. Admin routes (except the runner preview and the public activation and password reset
  // pages, which carry their own switch) keep the strip and restyle its toggle in the r5 look (`interface-controls--admin`).
  const admin = pathname.startsWith('/admin') && !exam && !isR5PublicPath(pathname)
  const className = exam ? 'interface-controls interface-controls--exam' : admin ? 'interface-controls interface-controls--admin' : isR5ShellPath(pathname) || isR5ToolPath(pathname) || isR5PublicPath(pathname) || pathname.startsWith('/__r5') ? 'interface-controls interface-controls--r5' : 'interface-controls'
  return <div className={className}>
    <LanguageToggle />
    <ReleaseBanner />
  </div>
}

export function AppRouter() {
  useLanguage()
  // Tasks and the first full mock a teacher chooses (ASSIGNMENTS-SPEC 11.1): without the build flag no route, wrapper or banner is added.
  const tasksOn = tasksEnabled()
  return (
    <BrowserRouter>
      <div className="interface-frame">
      <ViewAsBanner />
      <InterfaceControls />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/admin/activate" element={<ActivateStaffPage />} />
          <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
          <Route path="/ielts-score-calculator" element={<ScoreCalculatorsPage />} />
          <Route path="/where-can-my-score-take-me" element={<StudyDestinationsPage />} />
          {R5StoryPage && <Route path="/__r5" element={<R5StoryPage />} />}
          {R5MotionLab && <Route path="/__r5/motion" element={<R5MotionLab />} />}
          <Route path="/school/*" element={<SchoolEntry />} />
          <Route element={<RequireAuth />}>
            <Route element={<RequireRole role="student" />}>
              <Route element={<R5Shell />}>
                <Route path="/home" element={<DashboardPage />} />
                <Route path="/dashboard" element={<Navigate to="/home" replace />} />
                <Route path="/exams-library" element={tasksOn ? <><LibraryTaskBanner /><ExamsPage /></> : <ExamsPage />} />
                <Route path="/my-exams" element={<ResultsPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/results" element={<ResultsPage />} />
                <Route path="/results/:resultId" element={<ResultDetailPage />} />
                <Route path="/achievements" element={<AchievementsPage />} />
                <Route path="/plan" element={<PlanPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />
                {keepTalkingEnabled && <Route path="/keep-talking" element={<KeepTalkingPage />} />}
                {tasksOn && <Route path="/start" element={<OnboardingStartPage />} />}
              </Route>
              <Route path="/test/:id" element={tasksOn ? <TaskGate><RunnerPage /></TaskGate> : <RunnerPage />} />
            </Route>
            <Route element={<RequireRole role="admin" />}>
              <Route element={<StaffTicketBoundary />}>
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/exams" element={<Navigate to="/admin/exams/list" replace />} />
              {/* Every built admin surface lives inside the admin shell. Only
                  redirects and the no-write runner preview sit outside it. */}
              <Route element={<AdminShell />}>
                <Route path="/admin/support" element={<TicketInboxPage />} />
                <Route path="/admin/support/:ticketId" element={<TicketThreadPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
                <Route path="/admin/exams/list" element={<AdminExamsListPage />} />
                <Route path="/admin/exams/create" element={<AdminExamWizardPage />} />
                <Route path="/admin/exams/edit/:id" element={<AdminExamWizardPage />} />
                <Route path="/admin/exams/archived" element={<AdminExamsArchivedPage />} />
                <Route path="/admin/questions" element={<AdminQuestionBankPage />} />
                <Route path="/admin/exams/bulk-upload" element={<AdminBulkUploadRetiredPage />} />
                <Route path="/admin/submissions" element={<AdminSubmissionsPage />} />
                <Route path="/admin/needs-attention" element={<NeedsAttentionPage />} />
                <Route path="/admin/results/:id" element={<AdminResultReviewPage />} />
                <Route path="/admin/review-queue" element={<ReviewQueuePage />} />
                <Route path="/admin/review-queue/:itemId" element={<ReviewItemPage />} />
                <Route path="/admin/writing-review" element={<WritingReviewQueuePage />} />
                <Route path="/admin/writing-review/:resultRef" element={<WritingReviewPage />} />
                <Route path="/admin/students/list" element={<AdminStudentsListPage />} />
                <Route path="/admin/students/add" element={<AdminAddStudentPage />} />
                {/* After `add`, so `/admin/students/add` is never read as an id. */}
                <Route path="/admin/students/:id" element={<AdminStudentDetailPage />} />
                <Route path="/admin/admins/list" element={<AdminsListPage />} />
                <Route path="/admin/admins/add" element={<AdminAddAdminPage />} />
                {/* After `add` and `list`, so neither is ever read as an id. */}
                <Route path="/admin/admins/:employeeId" element={<AdminEmployeeDetailPage />} />
                <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
                {tasksOn && <Route path="/admin/tasks" element={<AdminTasksPage />} />}
              </Route>
              {/* Not `<Navigate to="…?status=unpublished">`: a constant target
                  discards the rest of the incoming query. */}
              <Route path="/admin/exams/drafts" element={<AdminDraftsRedirect />} />
              <Route path="/admin/students" element={<Navigate to="/admin/students/list" replace />} />
              <Route path="/admin/admins" element={<Navigate to="/admin/admins/list" replace />} />
              <Route path="/admin/tests/:id/preview" element={<RunnerPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </div>
    </BrowserRouter>
  )
}
